'use client'

import LanguageAndCurrencyPopup from '@/app/components/LanguageAndCurrencyMobile/LanguageAndCurrencyPopup'
import MobileOptionSelector, {
	MobileSelectorItem,
} from '@/app/components/LanguageAndCurrencyMobile/MobileOptionSelector'
import SelectorButton from '@/app/components/LanguageAndCurrencyMobile/SelectorButton'
import { Icons } from '@/app/components/ui/Icons'
import { INIT_DATA_STORAGE_KEY, TOP_CURRENCIES, HIDDEN_CURRENCY_WHEN_LOCAL_NOT_IN_LIST } from '@/app/constants'
import { Language } from '@/app/languages'
import { Currency, DestPagesProps, UserData } from '@/app/types'
import { useAllLanguages, useAllCurrencies } from '@/app/hooks/useLanguagesAndCurrencies'
import {
	useLanguageNavigation,
	useCurrencyNavigation,
	useCurrentLanguage,
	useCurrentCurrency,
	useCombinedLanguageAndCurrencyNavigation,
} from '@/app/hooks/useLanguageCurrencyNavigation'
import { useLocalStorage } from '@uidotdev/usehooks'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useParams } from 'next/navigation'
import { FC, useMemo, useState } from 'react'
import { CircleFlag } from 'react-circle-flags'

type LanguageAndCurrencySelectProps = {
	onClose?: () => void
}

const LanguageAndCurrencySelect: FC<LanguageAndCurrencySelectProps> = ({
	onClose,
}) => {
	const t = useTranslations('Header')
	const params = useParams<DestPagesProps['params']>()
	const [initData] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const defaultCurrency = initData?.defaultCurrency || TOP_CURRENCIES[0]

	// Use the centralized hook to get current currency (respects URL > selected > local > USD)
	const currentCurrencyCode = useCurrentCurrency()

	const { handleLanguageSelect } = useLanguageNavigation()
	const { handleCurrencySelect } = useCurrencyNavigation()
	const { handleBothSelect } = useCombinedLanguageAndCurrencyNavigation()
	const currentLanguage = useCurrentLanguage()

	const languages = useAllLanguages(true)
	const allCurrencies = useAllCurrencies()

	// Build currencies list with local currency at top if not in predefined list
	// Hide INR when a non-standard local currency is added
	const currencies = useMemo(() => {
		const allCurrenciesSet = new Set(allCurrencies.map((c) => c.abbreviation))

		let result = [...allCurrencies]

		if (!allCurrenciesSet.has(defaultCurrency.abbreviation)) {
			// Local currency doesn't exist in predefined list - hide INR
			const currenciesWithoutINR = allCurrencies.filter((c) => c.abbreviation !== HIDDEN_CURRENCY_WHEN_LOCAL_NOT_IN_LIST)
			result = [defaultCurrency, ...currenciesWithoutINR]
		}

		return result
	}, [allCurrencies, defaultCurrency])

	// Find the full currency object for the current currency code
	const currentCurrency = useMemo(() => {
		return currencies.find((c) => c.abbreviation === currentCurrencyCode) || defaultCurrency
	}, [currencies, currentCurrencyCode, defaultCurrency])

	const [isOpen, setIsOpen] = useState(false)
	const [isLanguageSelectOpen, setIsLanguageSelectOpen] = useState(false)
	const [isCurrencySelectOpen, setIsCurrencySelectOpen] = useState(false)

	const [tempLanguage, setTempLanguage] = useState<Language | null>(null)
	const [tempCurrency, setTempCurrency] = useState<Currency>(currentCurrency)

	const currentLanguageObj = useMemo(
		() => languages.find((lang) => lang.lang === currentLanguage) || null,
		[languages, currentLanguage]
	)

	const languageItems: MobileSelectorItem[] = useMemo(
		() =>
			languages.map((lang) => ({
				id: lang.lang,
				flag: lang.flag,
				title: lang.name,
				subtitle: lang.lang.toUpperCase(),
				searchableText: lang.englishName, // Enable searching by English name
			})),
		[languages]
	)

	const currencyItems: MobileSelectorItem[] = useMemo(
		() =>
			currencies.map((currency) => ({
				id: currency.abbreviation,
				flag: currency.flag,
				title: currency.abbreviation,
				subtitle: currency.label,
			})),
		[currencies]
	)

	const selectLanguage = (item: MobileSelectorItem) => {
		const language = languages.find((lang) => lang.lang === item.id)
		if (language) {
			setTempLanguage(language)
			setTimeout(() => setIsLanguageSelectOpen(false), 300)
		}
	}

	const selectCurrency = (item: MobileSelectorItem) => {
		const currency = currencies.find((curr) => curr.abbreviation === item.id)
		if (currency) {
			setTempCurrency(currency)
			setTimeout(() => setIsCurrencySelectOpen(false), 300)
		}
	}

	const handleSave = () => {
		const hasLanguageChanged =
			tempLanguage && tempLanguage.lang !== currentLanguage
		const hasCurrencyChanged =
			tempCurrency && tempCurrency.abbreviation !== currentCurrency.abbreviation
		const closePopup = () => {
			setIsOpen(false)
			if (onClose) onClose()
		}

		if (hasLanguageChanged && hasCurrencyChanged && tempLanguage) {
			handleBothSelect(tempLanguage.lang, tempCurrency, closePopup)
		} else if (hasLanguageChanged && tempLanguage) {
			handleLanguageSelect(tempLanguage.lang, closePopup)
		} else if (hasCurrencyChanged) {
			handleCurrencySelect(tempCurrency, closePopup)
		} else {
			closePopup()
		}
	}

	const handleOpenLanguageSelect = () => {
		setIsLanguageSelectOpen(!isLanguageSelectOpen)
		setIsCurrencySelectOpen(false)
	}

	const handleOpenCurrencySelect = () => {
		setIsCurrencySelectOpen(!isCurrencySelectOpen)
		setIsLanguageSelectOpen(false)
	}

	const handleClosePopup = () => {
		setIsOpen(false)
		if (onClose) onClose()
	}

	const handleOpenPopup = () => {
		setTempLanguage(currentLanguageObj)
		setTempCurrency(currentCurrency)
		setIsOpen(true)
	}

	const displayLanguage = tempLanguage || currentLanguageObj
	const displayCurrency = tempCurrency

	return (
		<>
			<button
				onClick={handleOpenPopup}
				className={`flex items-center justify-end w-10 h-10 p-2 -m-2 text-text-900`}
				aria-label="Open language and currency selector"
				data-testid="language-currency-open-btn-mobile"
			>
				<Icons.globeBig className="text-text-900" />
			</button>

			<LanguageAndCurrencyPopup
				isOpen={isOpen}
				onClose={handleClosePopup}
				onSave={handleSave}
			>
				<div className="flex flex-col w-full gap-[4px]">
					{/* <SelectorButton
						icon={<Icons.globeBig className="text-black" />}
						title={`${displayLanguage?.name} (${displayLanguage?.lang.toUpperCase()})`}
						subtitle={t('language')}
						isOpen={isLanguageSelectOpen}
						onClick={handleOpenLanguageSelect}
						ariaControls="language-selector"
						data-testid="language-selector-btn-mobile"
						langCode={displayLanguage?.lang}
					/> */}

					<MobileOptionSelector
						isOpen={isLanguageSelectOpen}
						items={languageItems}
						selectedItemId={tempLanguage?.lang || ''}
						onSelect={selectLanguage}
						id="language-selector-mobile"
						testId="language-selector"
						searchPlaceholder={t('searchForLanguage')}
						showIcon={false}
					/>

					<SelectorButton
						icon={<CircleFlag countryCode={displayCurrency.flag.toLowerCase()} />}
						title={`${displayCurrency.label} (${displayCurrency.abbreviation})`}
						subtitle={t('currency')}
						isOpen={isCurrencySelectOpen}
						onClick={handleOpenCurrencySelect}
						ariaControls="currency-selector"
						data-testid="currency-selector-btn-mobile"
					/>

					<MobileOptionSelector
						isOpen={isCurrencySelectOpen}
						items={currencyItems}
						selectedItemId={tempCurrency.abbreviation}
						onSelect={selectCurrency}
						id="currency-selector-mobile"
						testId="currency-selector"
						searchPlaceholder={t('searchForCurrency')}
					/>
				</div>
			</LanguageAndCurrencyPopup>
		</>
	)
}

export default dynamic(() => Promise.resolve(LanguageAndCurrencySelect), {
	ssr: false,
})
